import { useState } from "react";
import { createFileRoute, useRouterState } from "@tanstack/react-router";
import { BackLink } from "@/components/shared";
import { useAdaptedShape } from "@/lib/corpusShape";
import { CatalogDrawer } from "@/components/finding-aid/CatalogDrawer";
import { FindingAid } from "@/components/finding-aid/FindingAid";
import { getFindingAid } from "@/components/finding-aid/data";

export const Route = createFileRoute("/finding-aid/$entityId")({
  component: FindingAidPage,
});

function FindingAidPage() {
  const { entityId } = Route.useParams();
  // Live `/corpus/shape` adapted to the FE-internal `CorpusShape`, with
  // the bundled fixture as a defensive fallback. Shared with the graph
  // entrance and the context panel so all three see the same entities —
  // including ones the live endpoint surfaces beyond the curated
  // anchors. For anchors, `getFindingAid` overlays curator-grade
  // collection-level detail from `finding-aid.json` on top of whatever
  // shape source we're using.
  const { shape, isLoading } = useAdaptedShape();
  const entity = shape.entities.find((e) => e.entity_id === entityId);

  // The catalog drawer animation fires only when arriving from a
  // non-catalog mental model (graph / query), signalled via router
  // location state. A direct visit or refresh skips it.
  const cameFromEntrance = useRouterState({
    select: (s) => Boolean((s.location.state as { drawer?: boolean })?.drawer),
  });
  const [drawerDone, setDrawerDone] = useState(false);

  // On a hard refresh the live `/corpus/shape` is still in flight, so
  // `useAdaptedShape` hands back the bundled fixture — which only carries the
  // curated anchors, not the non-anchor entities (recipients, Arabic forms,
  // etc.). Treating that gap as "absent" flashed the not-found state for a
  // frame before the live shape landed. Hold a skeleton until the fetch
  // settles; only then is a missing entity genuinely missing.
  if (!entity && isLoading) {
    return <FindingAidLoading />;
  }

  if (!entity) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-16 text-center">
        <p className="text-caps uppercase tracking-caps text-ink-tertiary">Not found</p>
        <h1 className="mt-3 font-serif text-display text-ink-primary">
          No entity “{entityId}”
        </h1>
        <p className="mx-auto mt-3 max-w-md font-serif text-lede italic text-ink-secondary">
          That entity isn’t in the surveyed corpus.
        </p>
        <BackLink
          fallback={{ to: "/", search: { mode: "graph" } }}
          className="mt-6 inline-block text-meta text-ink-info hover:text-ink-primary"
        >
          ← Go back
        </BackLink>
      </div>
    );
  }

  const showDrawer = cameFromEntrance && !drawerDone;

  return (
    <div className="mx-auto max-w-6xl px-6 py-10 lg:px-10">
      {showDrawer && (
        <CatalogDrawer
          entity={entity}
          findingAid={getFindingAid(entity)}
          onComplete={() => setDrawerDone(true)}
        />
      )}
      <FindingAid entity={entity} />
    </div>
  );
}

/** Skeleton held while `/corpus/shape` loads, so a refresh never flashes the
 *  "No entity" state before the live shape resolves (aria-live for SR users). */
function FindingAidLoading() {
  return (
    <div
      className="mx-auto max-w-6xl px-6 py-10 lg:px-10"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="h-4 w-40 animate-pulse rounded bg-rule/20" />
      <div className="mt-4 h-10 w-2/3 animate-pulse rounded bg-rule/30" />
      <div className="mt-3 h-5 w-1/2 animate-pulse rounded bg-rule/20" />
      <div className="mt-8 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <div className="h-64 animate-pulse rounded-container bg-rule/20" />
        <div className="h-64 animate-pulse rounded-container bg-rule/20" />
      </div>
      <span className="sr-only">Loading finding aid…</span>
    </div>
  );
}
